// Permission checking utilities

// Store permissions in localStorage (would be from API in production)
const PERMISSIONS_KEY = 'nitroRankPermissions';

// Default permissions for each rank
const DEFAULT_RANK_PERMISSIONS = {
  founder: ['all'],
  'co-founder': ['manage_users', 'manage_giveaways', 'ban', 'lock', 'delete', 'moderate_chat', 'manage_announcements', 'view_analytics', 'manage_entries', 'manage_boosts'],
  manager: ['manage_giveaways', 'lock', 'moderate_chat', 'manage_announcements', 'view_analytics'],
  administrator: ['manage_users', 'ban', 'lock', 'moderate_chat', 'manage_announcements'],
  'head-admin': ['manage_users', 'ban', 'moderate_chat'],
  admin: ['lock', 'moderate_chat'],
  mod: ['moderate_chat'],
  user: [],
};

// Load permissions from storage or use defaults
export const getRankPermissions = (rank) => {
  try {
    const stored = localStorage.getItem(PERMISSIONS_KEY);
    if (stored) {
      const permissions = JSON.parse(stored);
      if (permissions[rank]) {
        return permissions[rank];
      }
    }
  } catch (error) {
    console.error('Failed to load permissions:', error);
  }
  return DEFAULT_RANK_PERMISSIONS[rank] || [];
};

// Save permissions to storage
export const saveRankPermissions = (rank, permissions) => {
  try {
    const stored = localStorage.getItem(PERMISSIONS_KEY);
    const allPermissions = stored ? JSON.parse(stored) : {};
    allPermissions[rank] = permissions;
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(allPermissions));
    return true;
  } catch (error) {
    console.error('Failed to save permissions:', error);
    return false;
  }
};

// Save all permissions at once
export const saveAllPermissions = (permissions) => {
  try {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
    return true;
  } catch (error) {
    console.error('Failed to save all permissions:', error);
    return false;
  }
};

// Get all permissions
export const getAllPermissions = () => {
  try {
    const stored = localStorage.getItem(PERMISSIONS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load all permissions:', error);
  }
  return DEFAULT_RANK_PERMISSIONS;
};

// Check if user has a specific permission
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  const permissions = getRankPermissions(user.role);
  
  // 'all' permission grants everything
  if (permissions.includes('all')) return true;
  
  return permissions.includes(permission);
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (user, permissionList) => {
  if (!user || !user.role) return false;
  
  const permissions = getRankPermissions(user.role);
  
  if (permissions.includes('all')) return true;
  
  return permissionList.some(perm => permissions.includes(perm));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (user, permissionList) => {
  if (!user || !user.role) return false;
  
  const permissions = getRankPermissions(user.role);
  
  if (permissions.includes('all')) return true;
  
  return permissionList.every(perm => permissions.includes(perm));
};

// Permission constants
export const PERMISSIONS = {
  ALL: 'all',
  MANAGE_USERS: 'manage_users',
  MANAGE_GIVEAWAYS: 'manage_giveaways',
  BAN: 'ban',
  LOCK: 'lock',
  DELETE: 'delete',
  MODERATE_CHAT: 'moderate_chat',
  MANAGE_ANNOUNCEMENTS: 'manage_announcements',
  VIEW_ANALYTICS: 'view_analytics',
  MANAGE_PERMISSIONS: 'manage_permissions',
  MANAGE_ENTRIES: 'manage_entries',
  MANAGE_BOOSTS: 'manage_boosts',
};

// Permission descriptions
export const PERMISSION_DESCRIPTIONS = {
  all: 'Full access to everything',
  manage_users: 'Edit user details, roles, and settings',
  manage_giveaways: 'Create, edit, and delete giveaways',
  ban: 'Ban users from the platform',
  lock: 'Lock user accounts',
  delete: 'Delete messages, giveaways, etc.',
  moderate_chat: 'Delete chat messages',
  manage_announcements: 'Create and edit announcements',
  view_analytics: 'Access analytics and statistics',
  manage_permissions: 'Edit rank permissions',
  manage_entries: 'Set user entry counts',
  manage_boosts: 'Add boosts to users',
};

